import React, { useEffect, useState } from "react";
import { toast } from "../../utils/toast";
import { checkPincodeServiceability } from "../../services/accountService";
import { PincodeServiceabilityResult } from "../../types/store";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
// TypeScript may not recognize side-effect CSS imports in some setups.
// @ts-ignore: Ignore missing module type declarations for CSS import
import "../../styles/shared/PincodeServiceChecker.css";

interface PincodeServiceCheckerProps {
  open: boolean;
  onClose: () => void;
  storageKey?: string;
  showFooterActions?: boolean;
}

const PINCODE_CHECKER_SESSION_KEY = "eldoo-pincode-checker-shown";

const PincodeServiceChecker: React.FC<PincodeServiceCheckerProps> = ({
  open,
  onClose,
  storageKey = PINCODE_CHECKER_SESSION_KEY,
  showFooterActions = true,
}) => {
  const [pincode, setPincode] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<PincodeServiceabilityResult | null>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPincode("");
    setChecking(false);
    setResult(null);
  }, [onClose, open, storageKey]);

  if (!open) {
    return null;
  }

  const handleCheck = async () => {
    const normalized = pincode.trim();
    if (!/^\d{6}$/.test(normalized)) {
      toast.error("Enter a valid 6-digit pincode.");
      return;
    }

    setChecking(true);
    try {
      const response = await checkPincodeServiceability(normalized);
      setResult(response);
      if (response.serviceable) {
        toast.success(response.message);
        window.sessionStorage.setItem(storageKey, "true");
        onClose();
      }
    } catch {
      toast.error("Unable to check serviceability right now.");
      setResult(null);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="pincode-checker-modal" role="presentation">
      <section
        className="pincode-checker-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pincode-checker-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="pincode-checker__copy">
          <span className="eyebrow">Pincode service</span>
          <h2 id="pincode-checker-title">Check home delivery availability</h2>
          <p>Enter your pincode to see whether Eldoo home delivery is available.</p>
        </div>

        <div className="pincode-checker__controls">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={pincode}
            onChange={(event) => {
              const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 6);
              setPincode(digitsOnly);
            }}
            placeholder="Enter pincode"
            aria-label="Enter pincode"
          />
          <button type="button" className="button" onClick={handleCheck} disabled={checking}>
            {checking ? "Checking..." : "Check"}
          </button>
        </div>

        {result ? (
          <div
            className={`pincode-checker__result ${
              result.serviceable ? "is-serviceable" : "is-unserviceable"
            }`}
          >
            <strong>{result.serviceable ? "Home delivery available" : "Home delivery is not available"}</strong>
            <span>{result.serviceable ? "You can continue with home delivery." : "Please choose another pincode or skip for now."}</span>
            {!result.serviceable ? (
              <small className="pincode-checker__note">Please skip for now or try another pincode.</small>
            ) : null}
          </div>
        ) : null}

        {showFooterActions ? (
          <div className="pincode-checker__footer">
            <button
              type="button"
              className="link-button"
              onClick={() => {
                window.sessionStorage.setItem(storageKey, "true");
                onClose();
              }}
              >
                Skip
              </button>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default PincodeServiceChecker;
